import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { AuthUserContext } from "../../../context/AuthUserContext";

const Card = styled.div`
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-large);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  position: relative;
`;
const InputFlex = styled(FlexDiv)`
  flex-direction: column;
  align-items: stretch;
  gap: var(--spacing-sm);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;
const Text = styled.h4`
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: var(--font-size-base);
  color: var(--text-100);
  text-transform: uppercase;
`;

const CancelText = styled(Text)`
  color: var(--primary-200);
  cursor: pointer;
`;
const Label = styled.label`
  font-size: var(--font-size-small);
  color: var(--text-200);
  font-weight: 300;
`;
const Input = styled.input`
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid transparent;
  border-radius: var(--border-radius-base);
  font-size: var(--font-size-small);
  font-weight: 700;
  background-color: var(--bg-200);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,
    background-color 0.15s ease-in-out;
  &::placeholder {
    text-transform: capitalize;
    color: var(--text-200);
    font-weight: 400;
  }
  &:disabled {
    background-color: var(--bg-200);
    cursor: not-allowed;
    opacity: 0.75;
  }
  &:focus {
    outline: none;
    background-color: var(--bg-100);
    border-color: var(--primary-200);
    box-shadow: 0 0 0 3px var(--primary-300);
  }

  @media (min-width: 768px) {
    width: 260px;
  }
`;
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  flex: 1;
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--bg-200);
  width: 100%;
`;
const SaveButton = styled.button`
  color: var(--bg-100);
  background-color: var(--primary-200);
  border-radius: var(--border-radius-large);
  border: none;
  &:hover {
    background-color: var(--primary-100);
  }
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  display: ${(props) => (props.$edit ? "flex" : "none")};

  @media (min-width: 768px) {
    width: auto;
    padding: var(--spacing-sm) var(--spacing-xxl);
  }
`;

const PersonalInformation = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const { userProfile, updateUserInfo } = useContext(AuthUserContext);
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [editName, setEditName] = useState(false);
  const [editMobile, setEditMobile] = useState(false);

  useEffect(() => {
    if (!userProfile) {
      return;
    }
    setName(userProfile.first_name || "");
    setLastName(userProfile.last_name || "");
    setEmail(userProfile.email || "");
    setMobile(userProfile.phone_number || "");
  }, [userProfile]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    const data = {
      first_name: name,
      last_name: lastName,
    };
    try {
      await updateUserInfo(data);
    } catch (error) {
      console.error("Greška prilikom ažuriranja", error);
    }
    setEditName(false);
  };
  const handleUpdateMobile = async (e) => {
    e.preventDefault();
    const data = {
      phone_number: mobile,
    };
    try {
      await updateUserInfo(data);
    } catch (error) {
      console.error("Greška prilikom ažuriranja", error);
    }
    setEditMobile(false);
  };

  return (
    <Card>
      <FlexDiv>
        <Text>{t("PROFILE.PERSONAL_INFORMATION")}</Text>
        {!editName ? (
          <CancelText onClick={() => setEditName(true)}>
            {t("BUTTONS.EDIT")}
          </CancelText>
        ) : (
          <CancelText onClick={() => setEditName(false)}>
            {t("BUTTONS.CANCEL")}
          </CancelText>
        )}
      </FlexDiv>
      <InputFlex>
        <InputGroup>
          <Label>{t("LABELS.NAME")}</Label>
          <Input
            type="text"
            placeholder={t("PLACEHOLDERS.NAME")}
            aria-label={t("LABELS.NAME")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!editName && !isMobile}
          />
        </InputGroup>
        <InputGroup>
          <Label>{t("LABELS.SURNAME")}</Label>
          <Input
            type="text"
            placeholder={t("PLACEHOLDERS.SURNAME")}
            aria-label={t("LABELS.SURNAME")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={!editName && !isMobile}
          />
        </InputGroup>
        <SaveButton
          $edit={editName}
          className="button-transparent"
          onClick={handleUpdate}
        >
          {t("BUTTONS.SAVE")}
        </SaveButton>
      </InputFlex>
      <Divider />
      <InputGroup>
        <Label>{t("LABELS.EMAIL")}</Label>
        <Input
          type="email"
          placeholder={t("PLACEHOLDERS.EMAIL")}
          aria-label={t("LABELS.EMAIL")}
          value={email}
          disabled
        />
      </InputGroup>
      <Divider />
      <FlexDiv>
        <Text>{t("LABELS.PHONE_NUMBER")}</Text>
        {!editMobile ? (
          <CancelText onClick={() => setEditMobile(true)}>
            {t("BUTTONS.EDIT")}
          </CancelText>
        ) : (
          <CancelText onClick={() => setEditMobile(false)}>
            {t("BUTTONS.CANCEL")}
          </CancelText>
        )}
      </FlexDiv>
      <InputFlex>
        <InputGroup>
          <Input
            type="text"
            placeholder={t("PLACEHOLDERS.PHONE_NUMBER")}
            aria-label={t("LABELS.PHONE_NUMBER")}
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            disabled={!editMobile && !isMobile}
          />
        </InputGroup>
        <SaveButton
          $edit={editMobile}
          className="button-transparent"
          onClick={handleUpdateMobile}
        >
          {isMobile ? t("BUTTONS.UPDATE") : t("BUTTONS.SAVE")}
        </SaveButton>
      </InputFlex>
    </Card>
  );
};

export default PersonalInformation;
